import { LiquidFlowRateUnits, GasFlowRateUnits } from "@/libs/units/flowRate";
import { GenericUnits } from "@/libs/units/generic";
import { LengthUnits, LargeLengthUnits } from "@/libs/units/length";
import { PercentageUnits } from "@/libs/units/percentage";
import { PowerUnits } from "@/libs/units/power";
import { PressureUnits } from "@/libs/units/pressure";
import { TemperatureUnits } from "@/libs/units/temperature";
import { EquipmentCalculatorSectionKey } from "../../calculationTypes";
import type { Section, SectionDeps } from "../types";
import { ScenarioDetailSectionKey, ScenarioDetailSectionTitle } from "../types";

export const buildIntakeConditionsSideCalculatorSection = ({
  scenarioCalculations,
  getHydraulicPowerUnit,
  getGasFlowUnit
}: SectionDeps): Section => ({
  id: EquipmentCalculatorSectionKey.INTAKE_CONDITIONS,
  hash: ScenarioDetailSectionKey.INTAKE,
  label: ScenarioDetailSectionTitle.INTAKE,
  routeName: "SizingParameters",
  aggregateDisplays: [
    {
      label: "Pump Intake Pressure",
      value: scenarioCalculations.pumpIntakePressurePSIA.value,
      base: PressureUnits.PoundsPerSquareInchAbsolute,
      model: PressureUnits.options(),
      mode: "convert"
    },
    {
      label: "Bottom of Intake Depth",
      value: scenarioCalculations.equipmentLengths.bottomOfIntakesDepthFt.value,
      base: LengthUnits.Feet,
      model: LargeLengthUnits.options(),
      mode: "convert"
    },
    {
      label: "Liquid Above Pump (TVD)",
      value:
        scenarioCalculations.intakeCalculations.aggregate.liquidAbovePumpTvdFt
          .value,
      base: LengthUnits.Feet,
      model: LargeLengthUnits.options(),
      mode: "convert"
    },
    {
      label: "Liquid Above Pump (MD)",
      value:
        scenarioCalculations.intakeCalculations.aggregate.liquidAbovePumpMDFt
          .value,
      base: LengthUnits.Feet,
      model: LargeLengthUnits.options(),
      mode: "convert"
    },
    {
      label: "Well Flowing Pressure (Datum)",
      value: scenarioCalculations.wellFlowingPressureAtDatumPSIA.value,
      base: PressureUnits.PoundsPerSquareInchAbsolute,
      model: PressureUnits.options(),
      mode: "convert"
    },
    {
      label: "Calculated Water Cut",
      base: PercentageUnits.Ratio,
      model: PercentageUnits.options(),
      target: PercentageUnits.Percentage,
      mode: "read",
      value: scenarioCalculations.surfaceFlowRates.value.waterCutRatio
    },
    {
      label: "Well Flowing Pressure (Perfs)",
      value: scenarioCalculations.wellFlowingPressureAtPerfsPSIA.value,
      base: PressureUnits.PoundsPerSquareInchAbsolute,
      model: PressureUnits.options(),
      mode: "convert"
    },
    {
      label: "Pump Intake Temperature",
      value: scenarioCalculations.pumpIntakeTemperatureF.value,
      base: TemperatureUnits.Fahrenheit,
      model: TemperatureUnits.options(),
      mode: "convert"
    },
    {
      label: "Oil Rate (in situ)",
      value:
        scenarioCalculations.fluidProperties.fluidPropertiesAtIntake.value
          .oilFlowRateBBLD,
      base: LiquidFlowRateUnits.BarrelsPerDay,
      model: LiquidFlowRateUnits.options(),
      mode: "convert"
    },
    {
      label: "Water Rate (in situ)",
      value:
        scenarioCalculations.fluidProperties.fluidPropertiesAtIntake.value
          .waterFlowRateBBLD,
      base: LiquidFlowRateUnits.BarrelsPerDay,
      model: LiquidFlowRateUnits.options(),
      mode: "convert"
    },
    {
      label: "Gas Rate @ Intake",
      value:
        scenarioCalculations.fluidProperties.fluidPropertiesAtIntake.value
          .freeGasFlowRateRCFD,
      base: GasFlowRateUnits.CubicFeetPerDay,
      target: getGasFlowUnit().value,
      model: GasFlowRateUnits.options(),
      mode: "convert"
    },
    {
      label: "Gas Rate @ Pump",
      value:
        scenarioCalculations.pumpCalculations.individual.value[
          scenarioCalculations.bottomPump.value?.id
        ]?.intakeStageCalculations?.intakeFluidPropertiesContext.fluidProperties
          .freeGasFlowRateRCFD,
      base: GasFlowRateUnits.CubicFeetPerDay,
      target: getGasFlowUnit().value,
      model: GasFlowRateUnits.options(),
      mode: "convert"
    },
    {
      label: "Natural Gas Separation",
      base: PercentageUnits.Ratio,
      target: PercentageUnits.Percentage,
      model: PercentageUnits.options(),
      mode: "read",
      value:
        scenarioCalculations.intakeCalculations.aggregate
          .naturalGasSeparationEfficiencyRatio.value
    },
    {
      label: "Gas Entering Intake",
      base: PercentageUnits.Ratio,
      target: PercentageUnits.Percentage,
      model: PercentageUnits.options(),
      mode: "read",
      value:
        scenarioCalculations.fluidProperties.fluidPropertiesAtIntake.value
          .gasFlowVolumeRatio
    },
    {
      label: "Gas Entering Pump",
      base: PercentageUnits.Ratio,
      target: PercentageUnits.Percentage,
      model: PercentageUnits.options(),
      mode: "read",
      value:
        scenarioCalculations.fluidProperties
          .fluidPropertiesAfterAllGasSeparationAtPump.value.gasFlowVolumeRatio
    },
    {
      label: "Total Flowrate Entering Pump",
      value:
        scenarioCalculations.pumpCalculations.individual.value[
          scenarioCalculations?.ascendingOrderPumps?.value[0]?.id
        ]?.intakeStageCalculations?.intakeFluidPropertiesContext.fluidProperties
          .fluidFlowRateBBLD,
      base: LiquidFlowRateUnits.BarrelsPerDay,
      model: LiquidFlowRateUnits.options(),
      mode: "convert"
    },
    {
      label: "Total Flowrate Entering Intake",
      value:
        scenarioCalculations.fluidProperties.fluidPropertiesAtIntake.value
          .fluidFlowRateBBLD,
      base: LiquidFlowRateUnits.BarrelsPerDay,
      model: LiquidFlowRateUnits.options(),
      mode: "convert"
    },
    {
      label: "Turpin Coefficient",
      value:
        scenarioCalculations.intakeCalculations.aggregate.turpinCoefficient
          .value,
      base: GenericUnits.Generic,
      model: GenericUnits.options(),
      mode: "read"
    },
    {
      label: "Intake HP",
      base: PowerUnits.HorsePower,
      target: getHydraulicPowerUnit().value,
      model: [PowerUnits.HorsePower],
      mode: "read",
      value: scenarioCalculations.intakeCalculations.aggregate.intakeHP.value
    }
  ],
  individualDisplays: scenarioCalculations.orderedIntakes.value.map(
    xrefIntake => {
      const calcs =
        scenarioCalculations.intakeCalculations.individual.value[xrefIntake.id];
      return {
        label: calcs.title,
        displays: [
          {
            label: "Intake Shaft HP Limit",
            base: PowerUnits.HorsePower,
            target: getHydraulicPowerUnit().value,
            model: [PowerUnits.HorsePower],
            value: calcs.xrefIntake.use_high_strength_shaft
              ? calcs.shaftPowerLimitHighStrengthHP
              : calcs.shaftPowerLimitStandardHP,
            mode: "read"
          },
          {
            label: "Shaft Load %",
            base: PercentageUnits.Ratio,
            target: PercentageUnits.Percentage,
            model: PercentageUnits.options(),
            value: calcs.xrefIntake.use_high_strength_shaft
              ? calcs.highStrengthShaftRatingRatio
              : calcs.standardShaftRatingRatio,
            mode: "read"
          }
        ]
      };
    }
  )
});
